/* ============================================================
   Pure helpers for deriving a post's display status from backend state.

   Extracted out of PostPreview.jsx's mapServerPost() (bug 2026-09-10):
   cancelling a schedule (MCP cancel_scheduled_post, or EditPost's
   "Save as draft" → api.scheduler.unschedule) sets scheduled_posts.status
   and every publish_targets.status to 'cancelled', and the scheduler
   re-syncs content_items.status back to 'ready' — it does NOT set
   archived_at. PostPreview used to map a representative target's
   'cancelled' status straight to the 'archived' UI status, so the post
   showed an "Archived" badge and lost its Schedule/Reschedule action even
   though the backend never archived it. Library's ContentFeedContext
   (buildContentCard's `status` — see publishSummaryStatus()) already got
   this right: it treats all-cancelled targets as 'ready' and derives
   'archived' ONLY from item.archivedAt.

   `archivedAt` is the ONLY source of truth for "archived" anywhere in the
   app — never a target status, never content_items.status itself. Keep
   that invariant when adding new callers.
   ============================================================ */

/* Map a single publish target's raw status to the UI's per-target display
   status. A cancelled schedule leaves the post 'ready' to be rescheduled —
   it is never itself an archive signal (see file banner above). */
export function targetStatusToUi(targetStatus, { platform = null, errorCode = null } = {}) {
  if (targetStatus === 'published') return 'published';
  if (targetStatus === 'needs_manual_action') {
    return platform === 'tiktok' && errorCode === 'TIKTOK_INBOX_ACTION_REQUIRED' ? 'inbox' : 'needs_check';
  }
  if (targetStatus === 'failed' || targetStatus === 'needs_reconnect') return 'failed';
  if (targetStatus === 'queued' || targetStatus === 'publishing') return 'publishing';
  if (targetStatus === 'pending' || targetStatus === 'scheduled') return 'scheduled';
  if (targetStatus === 'cancelled') return 'ready';
  return null;
}

/* Derive the post preview chrome's overall display status.
     archivedAt set        → 'archived', unconditionally (only source of truth)
     otherwise             → the representative target's mapped status,
                              falling back to the content item's own status,
                              falling back to `fallback` (default 'draft'). */
export function derivePostStatus({ targetStatus = null, targetPlatform = null, targetErrorCode = null, archivedAt = null, itemStatus = null, fallback = 'draft' } = {}) {
  if (archivedAt) return 'archived';
  const derived = targetStatus != null ? targetStatusToUi(targetStatus, { platform: targetPlatform, errorCode: targetErrorCode }) : null;
  return derived || itemStatus || fallback;
}

/* Whether a post is archived. archivedAt is the only source of truth —
   never derive this from a target status or a status string. */
export function isPostArchived(archivedAt) {
  return Boolean(archivedAt);
}
